"use client";

import clsx from "clsx";
import { useEffect, useRef, useState } from "react";

import { usePrefersReducedMotion } from "@/components/redesign/ChatSequence";
import { MarketingVisualBackdrop } from "@/components/redesign/MarketingVisualBackdrop";

export type EmailDraftCopy = {
  windowTitle: string;
  toLabel: string;
  toAddress: string;
  fromLabel: string;
  fromAddress: string;
  bccLabel: string;
  subject: string;
  greeting: string;
  /** Renewal paragraph, followed inline by the regulation citation chip. */
  renewalBody: string;
  citation: string;
  /** Discount sentence, split so the savings can be bolded. */
  discountIntro: string;
  discountEmphasis: string;
  closingBody: string;
  signOff: string;
  signature: string;
  approveLabel: string;
  editLabel: string;
};

/** Pause between the visual scrolling into view and the window opening. */
const OPEN_DELAY_MS = 250;

/**
 * Reveal order for the draft: recipients autofill quickly, then the message
 * is "written" top to bottom, and the review actions land last.
 */
const STEP = {
  to: 0,
  from: 1,
  subject: 2,
  greeting: 3,
  renewal: 4,
  discount: 5,
  closing: 6,
  signature: 7,
  actions: 8,
} as const;

/** Wait before each step appears (indexed by step). */
const REVEAL_DELAYS_MS = [400, 350, 600, 700, 900, 900, 900, 700, 600];

const STEP_COUNT = REVEAL_DELAYS_MS.length;

function ExpandDiagonalIcon({ className }: Readonly<{ className?: string }>) {
  return (
    <svg
      viewBox="0 0 12 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M7 1.5h3.5V5" />
      <path d="M10.5 1.5 6.8 5.2" />
      <path d="M5 10.5H1.5V7" />
      <path d="M1.5 10.5l3.7-3.7" />
    </svg>
  );
}

function CloseIcon({ className }: Readonly<{ className?: string }>) {
  return (
    <svg
      viewBox="0 0 12 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      className={className}
    >
      <path d="m1.5 1.5 9 9M10.5 1.5l-9 9" />
    </svg>
  );
}

function AlignIcon() {
  return (
    <svg
      viewBox="0 0 14 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      className="h-2.5 w-auto"
    >
      <path d="M1 2h12M1 6h8M1 10h12" />
    </svg>
  );
}

function UndoIcon({ flipped }: Readonly<{ flipped?: boolean }>) {
  return (
    <svg
      viewBox="0 0 14 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={clsx("h-2.5 w-auto", flipped && "-scale-x-100")}
    >
      <path d="M5 1.5 1.5 5 5 8.5" />
      <path d="M1.5 5h7a4 4 0 0 1 4 4v1.5" />
    </svg>
  );
}

function ListIcon({ ordered }: Readonly<{ ordered?: boolean }>) {
  return (
    <svg
      viewBox="0 0 14 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      className="h-2.5 w-auto"
    >
      <path d="M5 2h8M5 6h8M5 10h8" />
      {ordered ? (
        <path d="M1 1.5h1v2M1 5.5h1.5M1 9.5h1.5" strokeWidth="1.1" />
      ) : (
        <path d="M1.5 2h.01M1.5 6h.01M1.5 10h.01" strokeWidth="2.2" />
      )}
    </svg>
  );
}

function LinkIcon() {
  return (
    <svg
      viewBox="0 0 14 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      className="h-2.5 w-auto"
    >
      <path d="M6 8.5H4.5a2.5 2.5 0 0 1 0-5H6" />
      <path d="M8 3.5h1.5a2.5 2.5 0 0 1 0 5H8" />
      <path d="M4.5 6h5" />
    </svg>
  );
}

function SmileIcon() {
  return (
    <svg
      viewBox="0 0 12 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
      className="h-2.5 w-auto"
    >
      <circle cx="6" cy="6" r="5" />
      <path d="M4 7.2a2.6 2.6 0 0 0 4 0" />
      <path d="M4.3 4.5h.01M7.7 4.5h.01" strokeWidth="1.8" />
    </svg>
  );
}

function ImageIcon() {
  return (
    <svg
      viewBox="0 0 13 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-2.5 w-auto"
    >
      <rect x="1" y="1" width="11" height="10" rx="1.6" />
      <path d="m1.5 9 3-3 3.5 3.5" />
      <path d="m8 8 1.5-1.5L12 9" />
      <circle cx="8.7" cy="4" r="0.9" />
    </svg>
  );
}

function RecipientChip({
  address,
  className,
  avatarClassName,
}: Readonly<{
  address: string;
  className?: string;
  avatarClassName?: string;
}>) {
  return (
    <span
      className={clsx(
        "inline-flex max-w-full items-center gap-1 rounded-full py-0.5 pl-0.5 pr-2 text-[0.625rem] font-medium md:text-[0.6875rem]",
        className,
      )}
    >
      <span
        className={clsx(
          "h-3.5 w-3.5 shrink-0 rounded-full md:h-4 md:w-4",
          avatarClassName,
        )}
      />
      <span className="truncate">{address}</span>
    </span>
  );
}

/**
 * An email compose window that opens once it scrolls into view, then reveals
 * Gail's draft one section at a time — recipients, subject, paragraphs, and
 * finally the review actions. Every row keeps its layout space reserved so
 * the window never reflows; with reduced motion it renders statically.
 */
export function EmailComposeVisual({
  draft,
}: Readonly<{ draft: EmailDraftCopy }>) {
  const [opened, setOpened] = useState(false);
  const [shown, setShown] = useState(0);
  const [inView, setInView] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const reducedMotion = usePrefersReducedMotion();
  const open = reducedMotion || opened;

  // Arm once the visual scrolls into view.
  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.35 },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!inView || opened) return;
    const timeout = setTimeout(() => setOpened(true), OPEN_DELAY_MS);
    return () => clearTimeout(timeout);
  }, [inView, opened]);

  // Step through the draft, then stop — it plays only once.
  useEffect(() => {
    if (!opened || reducedMotion || shown >= STEP_COUNT) return;
    const timeout = setTimeout(
      () => setShown((current) => current + 1),
      REVEAL_DELAYS_MS[shown],
    );
    return () => clearTimeout(timeout);
  }, [opened, reducedMotion, shown]);

  const reveal = (step: number) =>
    reducedMotion || step < shown ? "animate-message-in" : "invisible";

  return (
    <div ref={containerRef} className="w-full">
      <div
        className={clsx(
          "flex w-full origin-bottom flex-col overflow-hidden rounded-xl bg-white text-left",
          "shadow-[0_24px_48px_-24px_rgba(15,40,80,0.45)]",
          "transition-[transform,opacity] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]",
          open ? "translate-y-0 scale-100 opacity-100" : "translate-y-6 scale-95 opacity-0",
          "[font-family:-apple-system,BlinkMacSystemFont,'Segoe_UI',Roboto,'Helvetica_Neue',sans-serif]",
        )}
      >
        <div className="flex shrink-0 items-center justify-between gap-3 bg-[#f2f2f3] px-3.5 py-2">
          <span className="truncate text-[0.625rem] font-medium text-black/75 md:text-[0.6875rem]">
            {draft.windowTitle}
          </span>
          <span className="flex shrink-0 items-center gap-2.5 text-black/70">
            <ExpandDiagonalIcon className="h-2.5 w-auto" />
            <CloseIcon className="h-2.5 w-auto" />
          </span>
        </div>

        <div className="px-3.5">
          <div className="flex items-center gap-2 border-b border-black/10 py-1.5">
            <span className="w-7 shrink-0 text-[0.625rem] text-black/70 md:text-[0.6875rem]">
              {draft.toLabel}
            </span>
            <RecipientChip
              address={draft.toAddress}
              className={clsx("bg-[#d3e8fd] text-[#1668d6]", reveal(STEP.to))}
              avatarClassName="bg-gradient-to-b from-[#7fb4f2] to-[#3d7fd4]"
            />
          </div>
          <div className="flex items-center gap-2 border-b border-black/10 py-1.5">
            <span className="w-7 shrink-0 text-[0.625rem] text-black/70 md:text-[0.6875rem]">
              {draft.fromLabel}
            </span>
            <RecipientChip
              address={draft.fromAddress}
              className={clsx("bg-[#f6dcf8] text-[#b13ec2]", reveal(STEP.from))}
              avatarClassName="bg-gradient-to-b from-[#e2a3e8] to-[#b35cc0]"
            />
            <span className="ml-auto shrink-0 text-[0.625rem] text-black/70 md:text-[0.6875rem]">
              {draft.bccLabel}
            </span>
          </div>
          <p
            className={clsx(
              "py-2.5 text-[0.8125rem] font-bold tracking-tight text-black md:text-[0.875rem]",
              reveal(STEP.subject),
            )}
          >
            {draft.subject}
          </p>
        </div>

        <div className="space-y-2.5 px-3.5 pb-3 text-[0.6875rem] leading-relaxed text-black/85 md:text-[0.75rem]">
          <p className={reveal(STEP.greeting)}>{draft.greeting}</p>
          <p className={reveal(STEP.renewal)}>
            {draft.renewalBody}{" "}
            <span className="inline-flex translate-y-px rounded bg-[#dcebfd] px-1 py-px text-[0.5625rem] font-semibold leading-snug text-[#1668d6]">
              {draft.citation}
            </span>
          </p>
          <p className={reveal(STEP.discount)}>
            {draft.discountIntro}
            <strong className="font-bold text-black">
              {draft.discountEmphasis}
            </strong>
          </p>
          <p className={reveal(STEP.closing)}>{draft.closingBody}</p>
          <p className={reveal(STEP.signature)}>
            {draft.signOff}
            <br />
            {draft.signature}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-2.5 border-t border-black/10 px-3.5 py-2 text-black/60">
          <AlignIcon />
          <span className="text-[0.6875rem] font-bold leading-none">B</span>
          <span className="text-[0.6875rem] italic leading-none">I</span>
          <span className="text-[0.6875rem] underline leading-none">U</span>
          <span aria-hidden className="h-3 w-px bg-black/10" />
          <UndoIcon />
          <UndoIcon flipped />
          <span aria-hidden className="h-3 w-px bg-black/10" />
          <ListIcon ordered />
          <ListIcon />
          <LinkIcon />
          <SmileIcon />
          <ImageIcon />
        </div>

        <div
          className={clsx(
            "flex shrink-0 items-center gap-2 px-3.5 pb-3.5 pt-1",
            reveal(STEP.actions),
          )}
        >
          <span className="rounded-md bg-gail-primary px-3 py-1.5 text-[0.625rem] font-semibold text-white md:text-[0.6875rem]">
            {draft.approveLabel}
          </span>
          <span className="rounded-md border border-black/15 bg-white px-3 py-1.5 text-[0.625rem] font-medium text-black/80 md:text-[0.6875rem]">
            {draft.editLabel}
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * Replicates the layout of `MarketingFeatureImageVisual` with `fit="contain"`,
 * but hosts the live compose window instead of an image.
 */
export function MarketingFeatureEmailVisual({
  label,
  draft,
  className,
}: Readonly<{
  /** Accessible description of the draft (replaces the old image alt). */
  label: string;
  draft: EmailDraftCopy;
  className?: string;
}>) {
  return (
    <div
      className={clsx(
        "relative aspect-4/3 w-full overflow-hidden rounded-2xl md:aspect-square md:rounded-3xl",
        className,
      )}
    >
      <MarketingVisualBackdrop />
      <span className="sr-only">{label}</span>
      <div
        aria-hidden
        className="absolute inset-0 flex items-center justify-center"
      >
        {/* The window is taller than the 4:3 mobile card, so anchor it to the
            top there (keeping the header/subject visible) and let the bottom
            crop; on md+ the square card fits it centered. */}
        <div className="flex h-full w-[78%] max-w-[360px] flex-col justify-start py-5 sm:w-[68%] md:justify-center md:py-8">
          <EmailComposeVisual draft={draft} />
        </div>
      </div>
    </div>
  );
}
